import { trackPromise } from 'react-promise-tracker';
import {
  acquireAdminConsent, getAccessTokenMicrosoft, outlookTokenRequest
} from '../context/microsoftProvider';
import { fetchWithTimeout, handleResponse } from './BaseAPI';
import User from '../context/User';

export const outlookUrl = process.env.REACT_APP_OUTLOOK_API;

function getAccessToken(email, interactive = false) {
  return getAccessTokenMicrosoft(email, outlookTokenRequest, interactive);
}

function requestHeaders(accessToken) {
  const requestHeaders = new Headers();
  requestHeaders.append('Accept', 'application/json');
  requestHeaders.append('Content-Type', 'application/json');
  requestHeaders.append("Authorization", `Bearer ${accessToken}`);
  return requestHeaders;
}

function doGet(url, accessToken) {
  return trackPromise(
    fetchWithTimeout(outlookUrl + url, {
      method: 'GET',
      headers: requestHeaders(accessToken),
      timeout: process.env.REACT_APP_OUTLOOK_REQUEST_TIMEOUT
    }).then(response => {
      return handleResponse(response);
    })
  );
}

function doPost(url, body, accessToken) {
  return trackPromise(
    fetchWithTimeout(outlookUrl + url, {
      method: 'POST',
      headers: requestHeaders(accessToken),
      body: JSON.stringify(body),
      timeout: process.env.REACT_APP_OUTLOOK_REQUEST_TIMEOUT
    }).then(response => {
      return handleResponse(response);
    })
  );
}

function doDelete(url, accessToken) {
  return trackPromise(
    fetchWithTimeout(outlookUrl + url, {
      method: 'DELETE',
      headers: requestHeaders(accessToken),
      timeout: process.env.REACT_APP_OUTLOOK_REQUEST_TIMEOUT
    }).then(response => {
      return handleResponse(response);
    })
  );
}

export async function verify() {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  return doGet(`/admin/${user.domain}/status?admin=${user.email}`, accessToken);
}

export async function authenticate() {
  const user = User.getUserInfo();
  // Open the consent window during the click, before awaiting a network request.
  await acquireAdminConsent(user);
  const accessToken = await getAccessToken(user.email);
  return doPost(`/admin/${user.domain}/auth`, { email: user.email }, accessToken);
}

export async function listAccounts(name, department, filterStatus, top, skiptoken) {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  const queryParams = new URLSearchParams();
  if (name && name.length > 0) {
    queryParams.set('name', name);
  }
  if (department && department.length > 0) {
    queryParams.set('department', department);
  }
  if (filterStatus) {
    queryParams.set('filterStatus', filterStatus.join(','));
  }
  if (top && top > 0) {
    queryParams.set('top', top);
  }
  if (skiptoken && skiptoken.length > 0) {
    // OutlookService forwards Graph's nextLink token in its URL-encoded form.
    let decodedToken = skiptoken;
    try { decodedToken = decodeURIComponent(skiptoken); } catch (error) { /* already opaque */ }
    queryParams.set('skiptoken', decodedToken);
  }
  return doGet(`/admin/${user.domain}/accounts?${queryParams}`, accessToken);
}

// Directory results are paginated, unlike the local group target list.
export async function listDirectoryAccounts() {
  const accounts = [];
  const tokens = new Set();
  let skiptoken = '';
  do {
    const result = await listAccounts(undefined, undefined, undefined, 100, skiptoken);
    if (!result || !Array.isArray(result.accounts) ||
        (result.skiptoken != null && typeof result.skiptoken !== 'string')) {
      throw new Error('Invalid directory response');
    }
    accounts.push(...result.accounts);
    skiptoken = result.skiptoken || '';
    if (skiptoken && tokens.has(skiptoken)) {
      throw new Error('Repeated directory pagination token');
    }
    tokens.add(skiptoken);
  } while (skiptoken);
  return accounts;
}

export async function saveAccounts(accounts) {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  return doPost(`/admin/${user.domain}/accounts`, accounts, accessToken);
}

export async function updateAccountStatus(account) {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  return doPost(`/admin/${user.domain}/account`, account, accessToken);
}

export async function deleteAccount(email) {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  return doDelete(`/admin/${user.domain}/account/${email}`, accessToken);
}

export async function searchAccounts(account) {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  let queryString = account.indexOf('@') >= 0 ? `email=${account}` : `domain=${account}`;
  return doGet(`/admin/accounts?${queryString}`, accessToken);
}

export async function unregisterAccounts(emails) {
  const user = User.getUserInfo();
  const accessToken = await getAccessToken(user.email);
  let accounts = [];
  for (let email of emails) {
    accounts.push({email: email, status: 'unregister'});
  }
  return doPost(`/admin/accounts`, accounts, accessToken);
}
